import {
	ApiAdapter,
	ApiAdapterMessage,
	ApiAdapterMessageRole,
	ApiAdapterStreamEvent,
	ApiAdapterThread,
} from "./ApiAdapter";
import { ApiKeyProvider } from "@/utilities/ApiKeyProvider";

import OpenAI, { NotFoundError, OpenAIError } from "openai";
import { Emitter, createNanoEvents } from "nanoevents";
import {
	ResponseInputMessageItem,
	ResponseOutputMessage,
} from "openai/resources/responses/responses";
import { buildAssistantInstructions } from "@/utilities/buildAssistantInstructions";

export class OpenaiResponsesApiAdapter implements ApiAdapter {
	private apiKeyProvider: ApiKeyProvider;
	private promptID: string;
	private vectorStoreId: string;
	private siteName: string;
	private siteTopics: string;
	private assistantPersonality: string;
	private proxyURL: string;
	private openai: OpenAI | null;

	constructor(
		apiKeyProvider: ApiKeyProvider,
		promptID: string,
		vectorStoreId: string,
		siteName: string,
		siteTopics: string,
		assistantPersonality: string,
		proxyURL: string,
		openai?: OpenAI,
	) {
		this.apiKeyProvider = apiKeyProvider;
		this.promptID = promptID;
		this.vectorStoreId = vectorStoreId;
		this.siteName = siteName;
		this.siteTopics = siteTopics;
		this.assistantPersonality = assistantPersonality;
		this.proxyURL = proxyURL;
		this.openai = openai ? openai : null;
	}

	private async getOpenAI(): Promise<OpenAI> {
		if (!this.apiKeyProvider.isValid || !this.openai) {
			const apiKey = await this.apiKeyProvider.get();
			this.openai = new OpenAI({
				apiKey: apiKey!,
				baseURL: this.proxyURL + "/ai",
				dangerouslyAllowBrowser: true,
			});
		}
		return this.openai;
	}

	private fromApiMessage(apiMsg: OpenAI.Conversations.Message): ApiAdapterMessage {
		let content = "";
		const annotations = [];

		if (Array.isArray(apiMsg.content)) {
			for (const block of apiMsg.content) {
				if ("text" in block) content += block.text;
				if ("annotations" in block) annotations.push(...block.annotations);
			}
		} else if (typeof apiMsg.content === "string") {
			content = apiMsg.content;
		}
		return {
			id: apiMsg.id,
			role: apiMsg.role as ApiAdapterMessageRole,
			content: content,
			annotations: annotations,
		};
	}

	private toApiMessage(
		adapterMsg: ApiAdapterMessage,
	): ResponseInputMessageItem | ResponseOutputMessage {
		const apiMsg = {} as ResponseInputMessageItem | ResponseOutputMessage;

		if (adapterMsg.id !== "") apiMsg.id = adapterMsg.id;
		apiMsg.role = adapterMsg.role;
		if (adapterMsg.role === "assistant") {
			apiMsg.content = [
				{
					type: "output_text",
					text: adapterMsg.content,
					annotations: adapterMsg.annotations,
				},
			];
		} else {
			apiMsg.content = [{ type: "input_text", text: adapterMsg.content }];
		}

		return apiMsg;
	}

	async init(): Promise<void> {
		await this.getOpenAI();
	}

	async fetchThread(threadID?: string): Promise<ApiAdapterThread> {
		const openai = await this.getOpenAI();
		if (threadID) {
			const items = await openai.conversations.items.list(threadID, {
				limit: 100,
				order: "asc",
			});
			const messages = items.data
				.filter((item: any) => item.type === "message")
				.map((item: any) => this.fromApiMessage(item as OpenAI.Conversations.Message))
				.reverse();
			return { id: threadID, messages: messages };
		} else {
			const thread = await openai.conversations.create();
			return { id: thread.id, messages: [] };
		}
	}

	async fetchMessage(threadID: string, messageID: string): Promise<ApiAdapterMessage> {
		const openai = await this.getOpenAI();

		const retrieve = () =>
			openai.conversations.items.retrieve(messageID, {
				conversation_id: threadID,
			});

		let item;
		try {
			item = await retrieve();
		} catch (error) {
			if (!(error instanceof NotFoundError)) throw error;

			console.log(`Message ${messageID} not found, retrying…`);
			await new Promise((resolve) => setTimeout(resolve, 250));
			item = await retrieve();
		}

		if (item.type !== "message")
			throw Error(`Server returned invalid conversation item type: ${item.type}`);
		return this.fromApiMessage(item);
	}

	async createMessage(threadID: string, message: ApiAdapterMessage): Promise<ApiAdapterMessage> {
		const openai = await this.getOpenAI();
		const createdItems = await openai.conversations.items.create(threadID, {
			items: [this.toApiMessage(message)],
		});
		const createdMessage = createdItems.data[0] as OpenAI.Conversations.Message;
		return this.fromApiMessage(createdMessage);
	}

	async startStream(threadID: string): Promise<Emitter<ApiAdapterStreamEvent>> {
		const openai = await this.getOpenAI();
		const eventStream = createNanoEvents<ApiAdapterStreamEvent>();
		/*
		// Old prompt method
		// Move the prompt itself into the code here
		const stream = openai.responses.stream({
			conversation: threadID,
			tool_choice: "required",
			tools: [
				{
					type: "file_search",
					vector_store_ids: [this.vectorStoreId],
				},
			],
			prompt: {
				id: "pmpt_69b88854e6b4819786f228a8e51138830a256f3b1c5fe814", //this.promptID,
				variables: {
					site_name: this.siteName,
					site_topics: this.siteTopics,
					assistant_personality: this.assistantPersonality,
				},
			},
			truncation: "auto",
			input: "",
		});
		*/
		const stream = openai.responses.stream({
			model: "gpt-5.4-mini",
			conversation: threadID,
			tool_choice: "required",
			tools: [
				{
					type: "file_search",
					vector_store_ids: [this.vectorStoreId],
				},
			],
			instructions: buildAssistantInstructions({
				siteName: this.siteName,
				siteTopics: this.siteTopics,
				assistantPersonality: this.assistantPersonality,
			}),
			truncation: "auto",
			input: "",
		});

		stream.on("response.created", () => {
			eventStream.emit("start");
		});

		let accumulatedDeltas = "";
		stream.on("response.output_text.delta", (event: OpenAI.Responses.ResponseTextDeltaEvent) => {
			accumulatedDeltas += event.delta;
			eventStream.emit("chunk", accumulatedDeltas);
		});

		stream.on("response.output_text.done", (event: OpenAI.Responses.ResponseTextDoneEvent) => {
			eventStream.emit("done", event.text, event.item_id);
		});

		stream.on("response.incomplete", () => {
			eventStream.emit("end");
		});

		stream.on("response.completed", () => {
			eventStream.emit("end");
		});

		stream.on("response.failed", (event: OpenAI.Responses.ResponseFailedEvent) => {
			const error = new Error(event.response.error?.message);
			eventStream.emit("error", error);
		});

		stream.on("error", (event: OpenAI.Responses.ResponseErrorEvent | OpenAIError) => {
			const error = new Error(event.message);
			eventStream.emit("error", error);
		});

		return eventStream;
	}
}
